import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ExtraInfoTable } from 'design-system/src/components/ExtraInfoTable/ExtraInfoTable';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { client } from '../../api';
import * as Sentry from '@sentry/react';
import { type OfficeDetail } from './types';

type RemoveOfficeModalProps = {
  /** Called when the modal should close. */
  onClose: () => void;
  /** The offices to remove (all rows sharing the same name/TIN). */
  offices: OfficeDetail[];
};

const RemoveOfficeModal: React.FC<RemoveOfficeModalProps> = ({ onClose, offices }) => {
  const queryClient = useQueryClient();
  const office = offices[0];

  const { mutate: deleteOffices, isPending } = useMutation({
    mutationFn: () =>
      client.post('/settings/offices/bulk-delete', {
        office_ids: offices.map((o) => o.id),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offices-v2'] });
      queryClient.invalidateQueries({ queryKey: ['unmatched-addresses'] });
      toast.custom(() => <Toast variant="success">Office removed</Toast>);
      onClose();
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  if (!office) return null;

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Remove this office?</ModalTitle>
      <ModalBody>
        <ExtraInfoTable
          data={[
            { label: 'Office name', value: office.name },
            { label: 'TIN', value: office.tin },
            { label: 'Addresses', value: offices.map((o) => o.address) },
          ]}
          marginTop="0"
          tableMarginBottom="0"
        />
        <Typography styledAs="bodyDMSans">
          Any addresses that belonged to this office will remain as unmatched addresses with no office assigned. You may
          reassign or remove these addresses from the unmatched addresses list.
        </Typography>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} disabled={isPending}>
          Back
        </Button>
        <Button variant="primary" isLoading={isPending} onClick={() => deleteOffices()}>
          Delete office
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RemoveOfficeModal;
